import React, { useEffect, useRef, useState } from 'react';
import styled from "styled-components";
import { BodyLarge } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { EditableAreaEntity } from '@features/desks/services/AdvanceHotDeskingService';

interface Props {
  area: EditableAreaEntity;
  onMouseDown: (e: any, area: EditableAreaEntity) => void;
  onMouseUp: (e: any, area: EditableAreaEntity) => void;
  onResize: (area: EditableAreaEntity, width: number, height: number) => void;
  mapScale: number;
}

export function EditableAreaSpot(props: Props) {
  const lastMousePos = useRef({x: 0, y: 0});
  const {area, onMouseDown, onMouseUp, onResize, mapScale} = props;

  const [isSizing, setIsSizing] = useState(false);
  const [width, setWidth] = useState(area.areaWidth ?? 200);
  const [height, setHeight] = useState(area.areaHeight ?? 150);

  useEffect(() => {
    if (area.areaWidth) {
      setWidth(area.areaWidth);
    }
    if (area.areaHeight) {
      setHeight(area.areaHeight);
    }
  }, [area.areaHeight, area.areaWidth]);

  const onDraggableMouseDown = (e: any) => {
    e.stopPropagation();
    setIsSizing(true);
  }

  const onDraggableMouseMove = (e: any) => {
    e.stopPropagation();
    const nowX = e.clientX;
    const nowY = e.clientY;
    if (isSizing) {
      const diffX = nowX - lastMousePos.current.x;
      const diffY = nowY - lastMousePos.current.y;
      setWidth(width + (diffX / mapScale));
      setHeight(height + (diffY / mapScale));
    }
    lastMousePos.current = {x: nowX, y: nowY};
  }

  const onDraggableMouseUp = (e: any) => {
    e.stopPropagation();
    if (isSizing) {
      setIsSizing(false);
      onResize(area, width, height);
    }
  }

  return (
    <AreaOverlay id={`area-${area.id}`}
                 x={area.areaX ?? 0} y={area.areaY ?? 0} height={height} width={width}
                 onMouseDown={(e: any) => onMouseDown(e, area)}
                 onMouseUp={(e: any) => onMouseUp(e, area)}>

      <BodyLarge weight={600}>{area.label}</BodyLarge>

      <DragableControl onMouseUp={onDraggableMouseUp}
                       onMouseLeave={onDraggableMouseUp}
                       onMouseDown={onDraggableMouseDown}
                       onMouseMove={onDraggableMouseMove} />

    </AreaOverlay>
  )
}

const AreaOverlay = styled.div<any>`
  height: ${props => `${props.height}px`};
  width: ${props => `${props.width}px`};
  background-color: ${Colours.transparent};
  position: absolute;
  left: ${props => `${props.x}px`};
  top: ${props => `${props.y}px`};
  border: 3px dashed ${Colours.blue};
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  transition: border .1s ease-in-out, background-color .1s ease-in-out;
  padding: 12px 16px;
  border-radius: 16px;
  &:hover {
    background: rgba(255, 255, 255, 0.6);
    mix-blend-mode: normal;
    border: 4px dashed ${Colours.blue};
    box-sizing: border-box;
    border-radius: 8px;
  }
`

const DragableControl = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  height: 32px;
  width: 32px;
  background-color: greenyellow;
`
